import type { AccountsData, Envelope, OverviewData, SpecialtiesData } from './types.js';
import { SPECIAL_REGIONS, UI_REGIONS, isUiRegion } from '../theme/tokens.js';

/**
 * The one place a served region that this client does not render is dropped.
 *
 * **Why anything has to be dropped at all.** `UI_REGIONS` is the frontend's set of six (SPA was
 * removed 2026-09-02 — see the note there), but pills, table rows, region chips and account
 * results are *served* collections: the BFF still builds them from its own seven-entry
 * `UI_REGIONS`, so SPA arrives in every one of them regardless of what the client believes. A
 * page that merely renders what it is given therefore renders a seventh region, and a page that
 * filters for itself is a rule copied into six components that will not stay in step.
 *
 * **Why here and not in the components.** `client.ts` is the only fetch layer, so a payload
 * scrubbed here is scrubbed once, for every reader of it — including the client store, which
 * caches these envelopes for six hours and hands them to pages that never call `api` themselves,
 * and the background prefetch, which fills that store before any page mounts. Filtering in a
 * component would leave SPA sitting in the cache for whatever reads it next.
 *
 * **What is deliberately NOT scrubbed.**
 *
 * - **`totals` / `totalsByCombo`.** They are the server's sums, and re-summing them here would
 *   mean reimplementing `RATIO_KEYS` — the rule that leaves Cov % and Conv % `null` in a totals
 *   row rather than folding seven coverages into a plausible 420%. SPA contributes nothing to
 *   any of them (no route in the warehouse reaches it), so the arithmetic is unaffected; the
 *   moment SPA carries data this becomes a real discrepancy and the removal belongs upstream.
 * - **The `ALL` chart scope.** It is a scope the timeseries route computes server-side over its
 *   own seven, not a list this client assembles. Same reasoning: SPA adds zero to every point.
 * - **Delivery's `scope.sub`.** The server composes that line as a *string* (`"Kapil Rao · R1,
 *   R5 · Nursing & Allied"`), and pattern-matching region codes back out of prose is not a
 *   filter, it is a guess. The badges beside it are filtered where they render, in
 *   `<DeliveryTable>`; a manager whose codes include SPA is not a case the roster can produce,
 *   because SPA has no recruiters.
 */

/**
 * The Clusters KPI card, counted from the set this client actually shows.
 *
 * ⚠ Replaces the served figure rather than filtering it: `clusters` is not a collection, it is
 * `{ regions, special, total }` computed on the server from *its* `UI_REGIONS` — so it would
 * read `7 · 5 Regions, 2 Special` above six chips, and the card that describes the navigation
 * set would be the one thing on the page disagreeing about how big it is. The card counts
 * clusters that **exist**, not clusters that are staffed (§9.7.1), which is exactly what a count
 * of this array is.
 */
const UI_CLUSTERS = {
  regions: UI_REGIONS.filter((code) => !SPECIAL_REGIONS.includes(code)).length,
  special: SPECIAL_REGIONS.length,
  total: UI_REGIONS.length,
} as const;

type SpecialtyRowByRegion = SpecialtiesData['rows'][number]['byRegion'];

/** Rewrap an envelope around new data, keeping every freshness field (FR-015). */
function withData<T>(envelope: Envelope<T>, data: T): Envelope<T> {
  return { ...envelope, data };
}

/** Pills, rows, and the Clusters card — the Overview's three region-shaped payloads. */
export function scopeOverview(envelope: Envelope<OverviewData>): Envelope<OverviewData> {
  const data = envelope.data;
  if (!data) return envelope;

  return withData(envelope, {
    ...data,
    pills: data.pills.filter((pill) => isUiRegion(pill.regionCode)),
    rows: data.rows.filter((row) => isUiRegion(row.regionCode)),
    kpi: data.kpi ? { ...data.kpi, clusters: { ...UI_CLUSTERS } } : data.kpi,
  });
}

/**
 * The region chips, and the per-row map the chips re-scope from.
 *
 * `byRegion` is narrowed as well as `regions`, because it is what the strip reads when a
 * discipline is selected (FR-S24) — leaving a SPA key in it would put the removed region back on
 * screen on the one interaction that does not re-request.
 */
export function scopeSpecialties(envelope: Envelope<SpecialtiesData>): Envelope<SpecialtiesData> {
  const data = envelope.data;
  if (!data) return envelope;

  return withData(envelope, {
    ...data,
    regions: data.regions.filter((chip) => isUiRegion(chip.code)),
    rows: data.rows.map((row) => ({
      ...row,
      byRegion: Object.fromEntries(
        Object.entries(row.byRegion).filter(([code]) => isUiRegion(code)),
      ) as SpecialtyRowByRegion,
    })),
  });
}

/**
 * ⌘K results, dropped rather than re-pointed.
 *
 * A result carries the region its client sits in and navigates to that region's detail page
 * (`navigate.ts`), so a SPA account would offer a route to a page this client no longer has.
 */
export function scopeAccounts(envelope: Envelope<AccountsData>): Envelope<AccountsData> {
  const data = envelope.data;
  if (!data) return envelope;

  return withData(envelope, {
    ...data,
    results: data.results.filter((result) => isUiRegion(result.regionCode)),
  });
}
